import { Lead } from '../types';
import { ApiError, apiJson } from './apiClient';
import { apiEndpoints, assertApiConfigured } from './env';

/** Backward-compatible names for webhook URLs */
export const N8N_WEBHOOKS = {
  get RETELL_QUEUE() {
    return apiEndpoints.retellQueueWebhook;
  },
  get ANALYTICS() {
    return apiEndpoints.analytics;
  },
};

function requireUrl(url: string, label: string): string {
  if (!url) {
    throw new Error(
      `Missing ${label}. Set VITE_API_BASE_URL in .env (e.g. https://your-worker.workers.dev).`,
    );
  }
  return url;
}

export const fetchAnalytics = async () => {
  assertApiConfigured();
  const url = requireUrl(apiEndpoints.analytics, 'analytics URL');
  return apiJson<unknown>(url, { method: 'GET' });
};

export const sendLeadToN8N = async (lead: Partial<Lead>, webhookUrl?: string) => {
  const url = webhookUrl || requireUrl(apiEndpoints.retellQueueWebhook, 'queue webhook URL');
  return apiJson<unknown>(url, {
    method: 'POST',
    body: JSON.stringify({
      ...lead,
      type: 'single',
      source: 'ReachAI-Frontend',
      sentAt: new Date().toISOString(),
    }),
  });
};

export const sendBulkLeadsToN8N = async (leads: Partial<Lead>[], webhookUrl?: string) => {
  const url = webhookUrl || requireUrl(apiEndpoints.retellQueueWebhook, 'queue webhook URL');
  const CHUNK_SIZE = 50;

  for (let i = 0; i < leads.length; i += CHUNK_SIZE) {
    const chunk = leads.slice(i, i + CHUNK_SIZE);
    try {
      await apiJson<unknown>(url, {
        method: 'POST',
        body: JSON.stringify({
          leads: chunk,
          type: 'bulk',
          source: 'ReachAI-Frontend-Bulk',
          batchId: crypto.randomUUID(),
          totalLeads: leads.length,
          chunkIndex: Math.floor(i / CHUNK_SIZE),
          sentAt: new Date().toISOString(),
        }),
      });
    } catch (e: unknown) {
      const chunkNum = Math.floor(i / CHUNK_SIZE) + 1;
      if (e instanceof ApiError) {
        const body = e.body;
        const details =
          typeof body === 'object' && body !== null && 'error' in body
            ? String((body as { error?: string }).error)
            : e.message;
        throw new Error(`Bulk chunk ${chunkNum} failed (${e.status}): ${details}`);
      }
      throw e;
    }
  }
  return true;
};
